import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from "@mui/icons-material";

function PasswordReset() {
    const navigate = useNavigate();
    const location = useLocation();
    const { username, primaryContactEmail } = location.state || {};

    // Password fields
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const handleClickShowPassword = () => setShowPassword((prev) => !prev);

    // Calculate password strength
    const getPasswordStrength = (pwd) => {
        let score = 0;
        if (pwd.length >= 8) score++;
        if (/[a-z]/.test(pwd)) score++;
        if (/[A-Z]/.test(pwd)) score++;
        if (/\d/.test(pwd)) score++;
        let label = "";
        let color = "";
        if (score <= 1) {
            label = "Weak";
            color = "bg-red-500";
        } else if (score === 2 || score === 3) {
            label = "Medium";
            color = "bg-yellow-500";
        } else if (score >= 4) {
            label = "Strong";
            color = "bg-green-600"; // Updated to a strong green
        }
        return { score, label, color, percentage: (score / 4) * 100 };
    };

    const strength = getPasswordStrength(password);
    const confirmStrength = getPasswordStrength(confirmPassword);

    // Redirect if no username/email in state
    React.useEffect(() => {
        if (!username || !primaryContactEmail) {
            navigate("/reset");
        }
    }, [username, primaryContactEmail, navigate]);

    const handleReset = (e) => {
        e.preventDefault();
        setErrorMessage("");
        setSuccessMessage("");

        if (password !== confirmPassword) {
            setErrorMessage("Passwords do not match. Please re-enter.");
            return;
        }

        const resetData = {
            username,
            primaryContactEmail,
            password,
        };

        axios.post("/password", resetData)
            .then((res) => {
                if (res.data.success) {
                    setSuccessMessage("Password has been successfully reset!");
                    // Redirect to login after 2 seconds
                    setTimeout(() => {
                        navigate("/");
                    }, 2000);
                } else {
                    setErrorMessage(res.data.message || "Error during password reset.");
                }
            })
            .catch((err) => {
                console.error("Reset error", err);
                setErrorMessage("Error during password reset process.");
            });
    };

    return (
        <div className="Homepage flex flex-col justify-center h-full bg-brand-tertiary">
            <div className="login-container">
                <div className="Homepage-page flex flex-col w-full max-w-4xl py-4 mx-auto my-auto">
                    {/* BOX 1: Title */}
                    <div className="bg-white rounded-2xl shadow p-6 text-black mb-4">
                        <h2 className="text-3xl font-arial normal-case no-underline">
                            Set New Password:
                        </h2>
                    </div>

                    {/* BOX 2: Form Fields */}
                    <div className="bg-white rounded-2xl shadow p-6 text-black mb-4">
                        {errorMessage && (
                            <div className="bg-red-100 text-red-700 px-4 py-2 rounded mb-4">
                                {errorMessage}
                            </div>
                        )}
                        {successMessage && (
                            <div className="bg-green-100 text-green-700 px-4 py-2 rounded mb-4">
                                {successMessage}
                            </div>
                        )}
                        <form id="resetForm" onSubmit={handleReset}>
                            <div className="flex flex-wrap -mx-2">
                                {/* New Password */}
                                <div className="w-full md:w-1/2 px-2 mb-4">
                                    <label className="block font-semibold mb-1">New Password</label>
                                    <div className="flex items-center border rounded">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Enter new password"
                                            className="w-full p-2 outline-none placeholder-gray-500"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={handleClickShowPassword}
                                            className="p-2"
                                        >
                                            {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                        </button>
                                    </div>
                                    {/* Password Strength Bar for New Password */}
                                    {password && (
                                        <div className="mt-2">
                                            <div className="h-2 w-full bg-gray-300 rounded">
                                                <div
                                                    className={`h-2 rounded ${strength.color}`}
                                                    style={{ width: `${strength.percentage}%` }}
                                                ></div>
                                            </div>
                                            <p className="text-sm mt-1">Strength: {strength.label}</p>
                                        </div>
                                    )}
                                </div>
                                {/* Confirm Password */}
                                <div className="w-full md:w-1/2 px-2 mb-4">
                                    <label className="block font-semibold mb-1">Confirm New Password</label>
                                    <div className="flex items-center border rounded">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="Re-enter new password"
                                            className="w-full p-2 outline-none placeholder-gray-500"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={handleClickShowPassword}
                                            className="p-2"
                                        >
                                            {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                        </button>
                                    </div>
                                    {/* Password Strength Bar for Confirm Password */}
                                    {confirmPassword && (
                                        <div className="mt-2">
                                            <div className="h-2 w-full bg-gray-300 rounded">
                                                <div
                                                    className={`h-2 rounded ${confirmStrength.color}`}
                                                    style={{ width: `${confirmStrength.percentage}%` }}
                                                ></div>
                                            </div>
                                            <p className="text-sm mt-1">Strength: {confirmStrength.label}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap justify-center mt-6">
                        <button
                            type="submit"
                            form="resetForm"
                            className="bg-gray-300 text-black rounded px-4 py-2 mx-2 w-full sm:w-auto"
                        >
                            Reset Password
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate("/")}
                            className="bg-gray-300 text-black rounded px-4 py-2 mx-2 w-full sm:w-auto"
                        >
                            Return to Home
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PasswordReset;
